import { PerformanceCallback, PerformanceCallbackMetadata } from '@amzn/aapn-front-end-apollo-client/es6/apollo_client_helper'
import { createAAPNApolloClient } from '@amzn/aapn-front-end-apollo-client'
import { ServerParseError } from '@apollo/client/link/http'
import { ServerError } from '@apollo/client/link/utils'
import introspectionQueryResultData from 'generated/fragmentTypes'
import { getMetricsPublisherChainAsync } from '@amzn/aapn-front-end-metrics'
import { ApolloClient } from '@apollo/client'
import { CSRFToken } from './fed_api_gateway_csrf_helper'
import { GraphQLError } from 'graphql'
import { pipe } from 'fp-ts/es6/function'
import { StageDiveConfig } from '../appconfig'
import { User } from 'oidc-client'
import * as RA from 'fp-ts/es6/ReadonlyArray'
import * as O from 'fp-ts/es6/Option'
import * as TE from 'fp-ts/es6/TaskEither'

export const PARTNER_ID_HEADER_KEY = 'Amazon-Advertising-API-AdvertiserId'
export const CUSTOMER_ID_HEADER_KEY = 'AAPN-Internal-Customer-Id'
const parseErrorType = (graphQLErrors?: ReadonlyArray<GraphQLError>, networkError?: any): { errorType: string; statusCode: number } =>
  pipe(
    O.fromNullable(networkError),
    O.fold(
      () =>
        pipe(
          O.fromNullable(graphQLErrors),
          O.fold(
            () => ({ errorType: 'none', statusCode: 200 }),
            (errors) =>
              pipe(
                errors,
                RA.filterMap((err) => O.fromNullable(err.extensions?.code)),
                RA.filter((code) => code === 'INTERNAL_SERVER_ERROR'),
                RA.match(
                  () => ({ errorType: 'client', statusCode: 400 }),
                  (_) => ({ errorType: 'server', statusCode: 500 }),
                ),
              ),
          ),
        ),
      (err) => ({ errorType: 'network', statusCode: (err as ServerParseError | ServerError)?.statusCode || 400 }),
    ),
  )

let isAdsConsoleIntEnabled = false
const onPerformanceData: PerformanceCallback = (latency: number, metadata: PerformanceCallbackMetadata): void => {
  const { operationName, graphQLErrors, networkError } = metadata
  const { errorType, statusCode } = parseErrorType(graphQLErrors, networkError)
  const errorCounter = graphQLErrors || networkError ? 1 : 0
  const successCounter = 1 - errorCounter

  const fetchWeblab = async (): Promise<void> => {
    if ((window as any).__AAC_CHROME_HIDE_WC_NAVS_PROMISE__ instanceof Promise) {
      const shouldHideChromeNav = await (window as any).__AAC_CHROME_HIDE_WC_NAVS_PROMISE__
      isAdsConsoleIntEnabled = !shouldHideChromeNav
    } else {
      isAdsConsoleIntEnabled = false
    }
  }

  fetchWeblab().then((r) => {
    if (isAdsConsoleIntEnabled) {
      getMetricsPublisherChainAsync(`AdsConsoleInt-ApolloClient.Request`).then((p) => {
        p
          //prettier-break
          .publishString(`operationName`, operationName)
          .publishTimer(`latency`, latency)
          .publishTimer(`${operationName}.latency`, latency)
          .publishCounter(`count`, 1)
          .publishCounter(`${operationName}.count`, 1)
          .publishCounter('success', successCounter)
          .publishCounter(`${operationName}.success`, successCounter)
          .publishCounter(`status.${statusCode}`, 1)
          .publishCounter(`${operationName}.status.${statusCode}`, 1)
          .publishCounter(`error`, errorCounter)
          .publishCounter(`${operationName}.error`, errorCounter)

        if (errorCounter) {
          p
            //prettier-break
            .publishCounter(`error.type.${errorType}`, 1)
            .publishCounter(`${operationName}.error.type.${errorType}`, errorCounter)
        }
      })
    }
  })

  getMetricsPublisherChainAsync(`ApolloClient.Request`).then((p) => {
    p
      //prettier-break
      .publishString(`operationName`, operationName)
      .publishTimer(`latency`, latency)
      .publishTimer(`${operationName}.latency`, latency)
      .publishCounter(`count`, 1)
      .publishCounter(`${operationName}.count`, 1)
      .publishCounter('success', successCounter)
      .publishCounter(`${operationName}.success`, successCounter)
      .publishCounter(`status.${statusCode}`, 1)
      .publishCounter(`${operationName}.status.${statusCode}`, 1)
      .publishCounter(`error`, errorCounter)
      .publishCounter(`${operationName}.error`, errorCounter)

    if (errorCounter) {
      p
        //prettier-break
        .publishCounter(`error.type.${errorType}`, 1)
        .publishCounter(`${operationName}.error.type.${errorType}`, errorCounter)
    }
  })
}

export const createStageDiveApolloClient = (config: StageDiveConfig, csrfToken: CSRFToken): TE.TaskEither<Error, ApolloClient<unknown>> => {
  const headers: { [key: string]: string } = {
    [PARTNER_ID_HEADER_KEY]: '',
    'Amazon-Advertising-API-CSRF-Data': config.apiClientId,
    'Amazon-Advertising-API-ClientId': config.apiClientId,
    'Amazon-Advertising-API-MarketplaceId': config.apiMarketplaceId,
    'Amazon-Advertising-API-CSRF-Token': csrfToken,
  }

  return createAAPNApolloClient(
    null as unknown as User, //The contract says that it can be null, and it is expected to be so in this case, but the compiler is not happy about it.
    config.apiEndpoint,
    headers,
    introspectionQueryResultData.possibleTypes,
    true,
    undefined,
    undefined,
    undefined,
    undefined,
    onPerformanceData,
  )
}
